import axios from 'axios';

// WordPress API configuration
// In production, set REACT_APP_WORDPRESS_URL to your Hostinger WordPress domain.
// In local dev, leave it unset — the proxy in package.json forwards requests to nothing-but-scuba-2.local.
const WORDPRESS_SITE_URL = process.env.REACT_APP_WORDPRESS_URL || '';
const WORDPRESS_API_BASE = `${WORDPRESS_SITE_URL}/wp-json/wp/v2`;

// Create axios instance with default config
const wpApi = axios.create({
  baseURL: WORDPRESS_API_BASE,
  timeout: 10000,
});

// WordPress API service
export const wordpressApi = {
  // Get all posts with pagination
  getPosts: async (page = 1, perPage = 10, category = null, tag = null) => {
    try {
      const params = {
        page,
        per_page: perPage,
        _embed: true, // Include featured images and other embedded data
        status: 'publish',
      };

      if (category && category !== 'all') {
        params.categories = category;
      }

      if (tag && tag !== 'all') {
        params.tags = tag;
      }

      const response = await wpApi.get('/posts', { params });

      // Guard: if response is not an array (e.g. wrong URL returns HTML), treat as empty
      if (!Array.isArray(response.data)) throw new Error('Unexpected API response');

      // Get total pages from headers
      const totalPages = parseInt(response.headers['x-wp-totalpages'] || 1);
      const totalPosts = parseInt(response.headers['x-wp-total'] || 0);

      return {
        posts: response.data,
        pagination: {
          currentPage: page,
          totalPages,
          totalPosts,
          hasNext: page < totalPages,
          hasPrev: page > 1,
        }
      };
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  },

  // Get a single post by slug
  getPostBySlug: async (slug) => {
    try {
      const response = await wpApi.get(`/posts`, {
        params: {
          slug,
          _embed: true,
          status: 'publish',
        }
      });
      
      return response.data[0] || null;
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  },

  // Get categories
  getCategories: async () => {
    try {
      const response = await wpApi.get('/categories', {
        params: {
          per_page: 100,
          hide_empty: true,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  // Get tags
  getTags: async () => {
    try {
      const response = await wpApi.get('/tags', {
        params: {
          per_page: 100,
          hide_empty: true,
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      throw error;
    }
  },

  // Resolve tag slugs to tag objects (the /posts endpoint filters by tag ID only,
  // so the homepage carousel has to look up its featured tags first)
  getTagsBySlugs: async (slugs = []) => {
    try {
      if (!slugs.length) return [];

      const response = await wpApi.get('/tags', {
        params: {
          slug: slugs, // axios serialises this as slug[]=…&slug[]=…
          per_page: 100,
        }
      });

      if (!Array.isArray(response.data)) throw new Error('Unexpected API response');
      return response.data;
    } catch (error) {
      console.error('Error fetching tags by slug:', error);
      throw error;
    }
  },

  // Get posts carrying any of the given tag IDs (one request for both featured slots —
  // the caller maps each post back to its slot via the post's own `tags` array)
  getPostsByTagIds: async (tagIds = []) => {
    try {
      if (!tagIds.length) return [];

      const response = await wpApi.get('/posts', {
        params: {
          tags: tagIds.join(','),
          per_page: 10,
          _embed: true,
          status: 'publish',
        }
      });

      if (!Array.isArray(response.data)) throw new Error('Unexpected API response');
      return response.data;
    } catch (error) {
      console.error('Error fetching posts by tag:', error);
      throw error;
    }
  },

  // Search posts
  searchPosts: async (searchTerm, page = 1, perPage = 10) => {
    try {
      const response = await wpApi.get('/posts', {
        params: {
          search: searchTerm,
          page,
          per_page: perPage,
          _embed: true,
          status: 'publish',
        }
      });

      if (!Array.isArray(response.data)) throw new Error('Unexpected API response');

      const totalPages = parseInt(response.headers['x-wp-totalpages'] || 1);
      const totalPosts = parseInt(response.headers['x-wp-total'] || 0);

      return {
        posts: response.data,
        pagination: {
          currentPage: page,
          totalPages,
          totalPosts,
          hasNext: page < totalPages,
          hasPrev: page > 1,
        }
      };
    } catch (error) {
      console.error('Error searching posts:', error);
      throw error;
    }
  },

  // Get featured image URL
  getFeaturedImageUrl: (post) => {
    if (post._embedded && post._embedded['wp:featuredmedia'] && post._embedded['wp:featuredmedia'][0]) {
      return post._embedded['wp:featuredmedia'][0].source_url;
    }
    return null;
  },

  // Get author information
  getAuthor: (post) => {
    if (post._embedded && post._embedded.author && post._embedded.author[0]) {
      return post._embedded.author[0];
    }
    return null;
  },

  // Get category information from a post
  getCategoriesFromPost: (post) => {
    if (post._embedded && post._embedded['wp:term']) {
      return post._embedded['wp:term'].flat().filter(term => term.taxonomy === 'category');
    }
    return [];
  },

  // Format post data for frontend
  formatPost: (post) => {
    const featuredImage = wordpressApi.getFeaturedImageUrl(post);
    const author = wordpressApi.getAuthor(post);
    const categories = wordpressApi.getCategoriesFromPost(post);

    return {
      id: post.id,
      title: post.title.rendered,
      excerpt: post.excerpt.rendered,
      content: post.content.rendered,
      slug: post.slug,
      date: post.date,
      modified: post.modified,
      author: author ? author.name : 'Unknown Author',
      authorId: author ? author.id : null,
      featuredImage,
      categories,
      link: post.link,
      readTime: Math.ceil(post.content.rendered.split(' ').length / 200), // Rough estimate
    };
  },

  // Format multiple posts
  formatPosts: (posts) => {
    return posts.map(post => wordpressApi.formatPost(post));
  },

  // Get all posts with only the fields needed for dive site map linking.
  // Uses _fields for a lightweight request — no content, no embedded media.
  getPostsForMap: async () => {
    try {
      const response = await wpApi.get('/posts', {
        params: {
          per_page: 100,
          status: 'publish',
          _fields: 'id,title,slug,meta',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching posts for map:', error);
      throw error;
    }
  },
};

export default wordpressApi; 